import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader } from "@/components/PageUI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Users, UserPlus, Trash2, Lock, Sparkles, Mail } from "lucide-react";

const label = (k) => k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ROLE_COLORS = { coach: "bg-sky-400/15 text-sky-500", manager: "bg-violet-400/15 text-violet-500", captain: "bg-amber-400/15 text-amber-500" };

const PremiumGate = () => (
  <div className="flex flex-col items-center justify-center text-center py-16 rounded-2xl border border-dashed border-border bg-card" data-testid="staff-gate">
    <div className="h-14 w-14 rounded-2xl brand-gradient flex items-center justify-center text-white mb-5"><Lock size={24} /></div>
    <h3 className="font-heading font-bold text-xl">Staff accounts are a Pro & Club feature</h3>
    <p className="text-muted-foreground mt-2 max-w-md">Invite coaches, managers and captains and control exactly what each can do.</p>
    <Button asChild className="mt-6 brand-gradient text-white border-0" data-testid="staff-upgrade-btn"><Link to="/app/payments"><Sparkles size={16} className="mr-2" /> Upgrade</Link></Button>
  </div>
);

function MemberRow({ m, allPerms, onSaved, onRemoved }) {
  const [role, setRole] = useState(m.club_role);
  const [perms, setPerms] = useState({ ...(m.permissions || {}) });
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setPerms((p) => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    setSaving(true);
    try { await api.put(`/staff/${m.id}/permissions`, { club_role: role, permissions: perms }); toast.success("Permissions saved"); onSaved(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${m.name || m.email}? Their account will be deleted.`)) return;
    try { await api.delete(`/staff/${m.id}`); toast.success("Staff removed"); onRemoved(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={`staff-member-${m.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full brand-gradient flex items-center justify-center text-white font-semibold">{(m.name || m.email)[0]?.toUpperCase()}</div>
          <div>
            <p className="font-semibold">{m.name || m.email}</p>
            <p className="text-xs text-muted-foreground">{m.email}</p>
          </div>
          <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${ROLE_COLORS[role] || ""}`}>{label(role)}</span>
        </div>
        <div className="flex items-center gap-2">
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-9 w-32" data-testid={`staff-role-${m.id}`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="coach">Coach</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="captain">Captain</SelectItem></SelectContent>
          </Select>
          <Button size="sm" onClick={save} disabled={saving} className="h-9 brand-gradient text-white border-0" data-testid={`staff-save-${m.id}`}>{saving ? "..." : "Save"}</Button>
          <button onClick={remove} className="text-muted-foreground hover:text-destructive p-2" data-testid={`staff-remove-${m.id}`}><Trash2 size={16} /></button>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-4 pt-4 border-t border-border">
        {allPerms.map((k) => (
          <label key={k} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!perms[k]} onChange={() => toggle(k)} className="h-4 w-4 accent-primary" data-testid={`staff-perm-${m.id}-${k}`} />
            {label(k)}
          </label>
        ))}
      </div>
    </div>
  );
}

export default function StaffPage() {
  const { isPremium } = useAuth();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", club_role: "coach" });
  const [inviting, setInviting] = useState(false);

  const load = () => api.get("/staff").then((r) => { setData(r.data); setForbidden(false); }).catch((e) => { if (e?.response?.status === 403) setForbidden(true); else toast.error(apiErr(e)); setData({ members: [], invites: [], all_permissions: [] }); });
  useEffect(() => { if (isPremium) load(); }, [isPremium]);

  const invite = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    setInviting(true);
    try { await api.post("/staff/invite", form); setForm({ email: "", name: "", club_role: "coach" }); load(); toast.success("Invitation sent"); }
    catch (e) { toast.error(apiErr(e)); } finally { setInviting(false); }
  };
  const revoke = async (id) => { try { await api.delete(`/staff/invite/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Staff & permissions" subtitle="Invite coaches, managers and captains — and control exactly what each can do." testid="staff-header" />
      {!isPremium ? <PremiumGate /> : forbidden ? (
        <div className="text-center py-16 rounded-2xl border border-dashed border-border" data-testid="staff-owner-only">
          <Lock size={22} className="mx-auto text-muted-foreground mb-2" />
          <p className="font-heading font-semibold">Only the club owner can manage staff</p>
        </div>
      ) : data === null ? <Loader /> : (
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6" data-testid="staff-invite-form">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><UserPlus size={18} /> Invite a staff member</h3>
            <div className="grid sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2"><Label className="text-xs">Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="coach@email.com" className="mt-1 h-10" data-testid="staff-invite-email" /></div>
              <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Alex" className="mt-1 h-10" data-testid="staff-invite-name" /></div>
              <div><Label className="text-xs">Role</Label>
                <Select value={form.club_role} onValueChange={(v) => setForm({ ...form, club_role: v })}>
                  <SelectTrigger className="mt-1 h-10" data-testid="staff-invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="coach">Coach</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="captain">Captain</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={invite} disabled={inviting} className="mt-3 brand-gradient text-white border-0" data-testid="staff-invite-btn"><Mail size={15} className="mr-1" /> {inviting ? "Sending..." : "Send invitation"}</Button>
            <p className="text-xs text-muted-foreground mt-2">They'll start with sensible defaults for the role — you can fine-tune every permission below once they join.</p>
          </div>

          {data.invites?.length > 0 && (
            <div>
              <h3 className="font-heading font-semibold mb-2 text-sm text-muted-foreground">Pending invitations</h3>
              <div className="space-y-2">
                {data.invites.map((iv) => (
                  <div key={iv.id} className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3" data-testid={`staff-invite-${iv.id}`}>
                    <div><p className="text-sm font-medium">{iv.email}</p><p className="text-xs text-muted-foreground">{label(iv.club_role)} · invited</p></div>
                    <button onClick={() => revoke(iv.id)} className="text-xs text-destructive hover:underline" data-testid={`revoke-invite-${iv.id}`}>Revoke</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <h3 className="font-heading font-semibold flex items-center gap-2"><Users size={18} /> Team ({data.members?.length || 0})</h3>
            {(!data.members || data.members.length === 0) ? (
              <p className="text-sm text-muted-foreground">No staff yet. Invite your first coach, manager or captain above.</p>
            ) : data.members.map((m) => (
              <MemberRow key={m.id} m={m} allPerms={data.all_permissions} onSaved={load} onRemoved={load} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
